<script setup lang="ts">
import { useTemplateRef } from "vue";

const { isLoggedIn, logout, user } = useUser();
const accountMenu = useTemplateRef("accountMenu");
const router = useRouter();

const loginModalController = useModal();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const isAccountMenuOpen = ref(false);

import { useModalStore } from "@/stores/modal";

const modalStore = useModalStore();

// Watch for login modal opening and trigger modalStore.openModal()
watch(loginModalController.isOpen, (isOpen: boolean) => {
  if (isOpen) {
    modalStore.openModal();
  } else {
    modalStore.closeModal();
  }
});

onClickOutside(accountMenu, () => {
  isAccountMenuOpen.value = false;
});

async function invokeLogout() {
  try {
    await logout();
  } finally {
    isAccountMenuOpen.value = false;
  }
}

async function invokeResetPassword() {
  await router.push(formatLink("/account/recover"));
  loginModalController.close();
}

// Function to close account menu
function closeAccountMenu() {
  isAccountMenuOpen.value = false;
}

</script>
<template>
  <ClientOnly>
    <div v-show="!isLoggedIn">
      <button
        class="action-button h-[20px] w-[20px] flex items-center justify-center account-button hover:text-primary focus:text-primary transition-colors duration-300 ease-in-out"
        data-testid="header-sign-in-link"
        @click="loginModalController.open()"
        :aria-label="$t('header.arialLabelSignin')"
      >
        <i class="icon-person-outline" aria-hidden="true"></i>
        <span class="sr-only">{{ $t('header.arialLabelSignin') }}</span>
      </button>
    </div>
    <SharedModal :controller="loginModalController">
      <AccountLoginForm
        @close="loginModalController.close"
        @success="loginModalController.close"
      >
        <div class="flex items-center justify-end">
          <NuxtLink
              class="text-primary underline font-base cursor-pointer"
              @click.stop="invokeResetPassword"
            >
            {{ $t("recoveryPassword.forgotPassword") }}
          </NuxtLink>
        </div>
      </AccountLoginForm>
    </SharedModal>
    <div v-if="isLoggedIn">
        <div class="flex items-center justify-center h-[20px] w-[20px]">
          <NuxtLink
            :to="formatLink('/account')"
            class="action-button sm:hidden h-[20px] w-[20px] flex items-center justify-center account-button hover:text-primary focus:text-primary transition-colors duration-300 ease-in-out"
            data-testid="account-menu-hello-button"
            :aria-label="$t('account.menu.myAccount')"
          >
              <i class="icon-person-outline" aria-hidden="true"></i>
              <span class="sr-only">{{ $t('account.menu.myAccount') }}</span>
          </NuxtLink>

          <button
            type="button"
            class="action-button hidden sm:flex h-[20px] w-[20px] flex items-center justify-center account-button hover:text-primary focus:text-primary transition-colors duration-300 ease-in-out"
            data-testid="account-menu-hello-button"
            @click="isAccountMenuOpen = !isAccountMenuOpen"
            :aria-label="$t('account.menu.openMenu')"
            aria-labelledby="account-menu-label"
          >
              <i class="icon-person-outline" aria-hidden="true"></i>
              <span id="account-menu-label" class="sr-only">{{ $t('account.menu.openMenu') }}</span>
          </button>
        </div>

        <!-- Profile dropdown -->
        <div class="relative">
          <div>
            <button
              id="user-menu-button"
              type="button"
              class="bg-secondary-800 flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-secondary-800 focus:ring-white"
              aria-expanded="false"
              aria-haspopup="true"
              @click="isAccountMenuOpen = !isAccountMenuOpen"
            >
              <span class="sr-only">{{ $t("account.menu.openMenu") }}</span>
            </button>
          </div>
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div
              v-if="isAccountMenuOpen"
              ref="accountMenu"
              :class="[isAccountMenuOpen ? 'block' : 'hidden']"
              class="z-20 origin-top-right absolute right-0 top-2 mt-2 w-48 rounded-md shadow-2xl px-2 py-4 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="user-menu-button"
              tabindex="-1"
            >
              <span class="text-md font-bold leading-[20px] text-secondary-700 bg-transparent focus:outline-none hidden sm:block px-4 pb-4">{{ $t("account.menu.hello") }}, {{ user?.firstName }}!</span>
              <hr/> 
              <NuxtLink
                id="user-menu-item-1"
                :to="formatLink('/account')"
                data-testid="header-my-account-link"
                class="mt-2 flex items-center px-4 py-1 text-base text-secondary-700 hover:text-primary w-full text-left"
                tabindex="-1"
                @click="closeAccountMenu"
              >
                <!-- <i class="icon-overview text-base mr-2" aria-hidden="true"></i> -->
                {{ $t("account.menu.myAccount") }}
              </NuxtLink>
              <NuxtLink
                id="user-menu-item-2"
                :to="formatLink(`/account/profile`)"
                data-testid="header-my-profile-link"
                class="flex items-center px-4 py-1 text-base text-secondary-700 hover:text-primary w-full text-left"
                tabindex="-1"
                @click="closeAccountMenu"
              >
                <!-- <i class="icon-verified-profile text-base mr-2" aria-hidden="true"></i> -->
                {{ $t("account.yourProfile") }}
              </NuxtLink>
              <NuxtLink
                id="user-menu-item-3"
                :to="formatLink(`/account/address`)"
                data-testid="header-my-address-link"
                class="flex items-center px-4 py-1 text-base text-secondary-700 hover:text-primary w-full text-left"
                tabindex="-1"
                @click="closeAccountMenu"
              >
                <!-- <i class="icon-warehouse-alt text-base mr-2" aria-hidden="true"></i> -->
                {{ $t("account.yourAddress") }}
              </NuxtLink>
              <NuxtLink
                id="user-menu-item-4"
                :to="formatLink(`/account/order`)"
                data-testid="header-my-order-link"
                class="flex items-center px-4 py-1 text-base text-secondary-700 hover:text-primary w-full text-left"
                tabindex="-1"
                @click="closeAccountMenu"
              >
                <!-- <i class="icon-orders text-base mr-2" aria-hidden="true"></i> -->
                {{ $t("account.orderHistoryHeader") }}
              </NuxtLink>
              <button
                id="user-menu-item-2"
                data-testid="header-sing-out-link"
                class="flex items-center px-4 py-1 text-base text-secondary-700 hover:text-primary w-full text-left"
                role="menuitem"
                tabindex="-2"
                @click="invokeLogout"
              >
                <!-- <i class="icon-logout text-base mr-2" aria-hidden="true"></i> -->
                {{ $t("account.menu.signOut") }}
              </button>
            </div>
          </Transition>
        </div>
    </div>
  </ClientOnly>
</template>
